import { useState } from 'react';
import { View, Modal, Pressable, StyleSheet } from 'react-native';
import { Feather } from '@expo/vector-icons';

import AppStyles from '../AppStyles';

import CustomText from './CustomText';
import AppTextInput from './AppTextInput';
import AppButton from './AppButton';
import { useTranslation } from '../i18n';

/**
 * DeleteAccountModal - Confirmation modal for self-deleting account
 * Requires password re-entry for security
 */
const DeleteAccountModal = ({
  visible,
  onClose,
  onConfirm,
  isDeleting = false,
}) => {
  const { t } = useTranslation(['admin', 'common']);

  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  const handleConfirm = async () => {
    if (!password.trim()) {
      setError(t('deleteAccount.passwordRequired'));
      return;
    }
    setError('');
    try {
      await onConfirm(password);
    } catch (err) {
      setError(err.message || t('deleteAccount.failed'));
    }
  };

  const handleClose = () => {
    setPassword('');
    setError('');
    onClose();
  };

  return (
    <Modal
      visible={visible}
      animationType="fade"
      transparent={true}
      onRequestClose={handleClose}
    >
      <View style={styles.overlay}>
        <View style={styles.modalContainer}>
          <View style={styles.header}>
            <CustomText font="semiBold" size={18} style={styles.title}>
              {t('deleteAccount.title')}
            </CustomText>
            <Pressable
              onPress={handleClose}
              style={styles.closeButton}
              accessibilityRole="button"
              accessibilityLabel={t('deleteAccount.closeLabel')}
              disabled={isDeleting}
            >
              <Feather name="x" size={24} color={AppStyles.colorSet.textSecondary} />
            </Pressable>
          </View>

          <View style={styles.content}>
            <View style={styles.warningBox}>
              <Feather name="alert-triangle" size={24} color={AppStyles.colorSet.error} />
              <CustomText size={14} style={styles.warningText}>
                {t('deleteAccount.warning')}
              </CustomText>
            </View>

            <CustomText size={14} style={styles.instructionText}>
              {t('deleteAccount.prompt')}
            </CustomText>

            <AppTextInput
              leftIcon="lock"
              placeholder={t('deleteAccount.passwordPlaceholder')}
              secureTextShowHide={true}
              value={password}
              onChangeText={setPassword}
              error={!!error}
              errorMessage={error}
              fullWidth
              editable={!isDeleting}
            />
          </View>

          <View style={styles.actions}>
            <AppButton
              title={t('common:actions.cancel')}
              onPress={handleClose}
              isEnabled={!isDeleting}
              buttonStyle={styles.cancelButton}
              textStyle={styles.cancelButtonText}
            />
            <AppButton
              title={t('common:actions.delete')}
              onPress={handleConfirm}
              isBusy={isDeleting}
              isEnabled={!isDeleting && password.length > 0}
              buttonStyle={styles.deleteButton}
              textStyle={styles.deleteButtonText}
            />
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: AppStyles.spacing.xl,
  },
  modalContainer: {
    backgroundColor: AppStyles.colorSet.cardBackground,
    borderRadius: AppStyles.borderRadii.lg,
    width: '100%',
    maxWidth: 400,
    ...AppStyles.styleSet.shadowLarge,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: AppStyles.spacing.lg,
    borderBottomWidth: 1,
    borderBottomColor: AppStyles.borderSet.light,
  },
  title: {
    color: AppStyles.colorSet.error,
  },
  closeButton: {
    padding: AppStyles.spacing.xs,
  },
  content: {
    padding: AppStyles.spacing.lg,
  },
  warningBox: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: AppStyles.colorSet.errorLight,
    padding: AppStyles.spacing.md,
    borderRadius: AppStyles.borderRadii.md,
    marginBottom: AppStyles.spacing.lg,
    gap: AppStyles.spacing.sm,
  },
  warningText: {
    flex: 1,
    color: '#991B1B',
  },
  instructionText: {
    marginBottom: AppStyles.spacing.md,
    color: AppStyles.colorSet.textSecondary,
  },
  actions: {
    flexDirection: 'row',
    padding: AppStyles.spacing.lg,
    paddingTop: 0,
    gap: AppStyles.spacing.sm,
  },
  cancelButton: {
    flex: 1,
    backgroundColor: 'transparent',
    borderWidth: 1,
    borderColor: AppStyles.colorSet.textSecondary,
    borderRadius: AppStyles.borderRadii.md,
    paddingVertical: 14,
    paddingHorizontal: 16,
  },
  cancelButtonText: {
    color: AppStyles.colorSet.textSecondary,
  },
  deleteButton: {
    flex: 1,
    backgroundColor: AppStyles.colorSet.error,
    borderRadius: AppStyles.borderRadii.md,
    paddingVertical: 14,
    paddingHorizontal: 16,
  },
  deleteButtonText: {
    color: '#7F1D1D', // Dark red - good contrast on both enabled and disabled states
  },
});

export default DeleteAccountModal;
